import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import css from './roadMap.module.css';
import Header from '../../components/standartComponents/header';
import { useTranslation } from 'react-i18next';

/* ========== DATA ========== */
const LEVELS = [
  {from:'pre-A1',to:'A0',desc:'Починаю з абсолютного нуля'},
  {from:'A0',to:'A1',desc:'Починаю з основ'},
  {from:'A1',to:'A2',desc:'Знаю базові слова та прості фрази'},
  {from:'A2',to:'B1',desc:'Розумію прості діалоги'},
  {from:'B1',to:'B2',desc:'Можу говорити на знайомі теми'},
  {from:'B2',to:'C1',desc:'Вільно спілкуюся в більшості ситуацій'}
];

const TOPICS = [
  {id:'work',emoji:'💼',title:'Робота',desc:'Співбесіди, зустрічі, листування та робочі діалоги.'},
  {id:'travel',emoji:'✈️',title:'Подорожі',desc:'Аеропорт, готель, кафе та знайомства за кордоном.'},
  {id:'study',emoji:'🎓',title:'Навчання',desc:'Освітні теми, пояснення та академічна лексика.'},
  {id:'talk',emoji:'💬',title:'Спілкування',desc:'Знайомства, small talk і щоденні розмови.'},
  {id:'self',emoji:'🌿',title:'Для себе',desc:'Теми з життя, хобі та легка практика.'},
  {id:'tech',emoji:'📰',title:'Новини й технології',desc:'Сучасні теми, технології та обговорення.'},
  {id:'health',emoji:'🩺',title:'Здоров\'я',desc:'Лікар, аптека та базові ситуації.'},
  {id:'leisure',emoji:'🎯',title:'Дозвілля',desc:'Плани, вихідні, розваги та зустрічі.'}
];

const lessons = [
  { num:1, title:'Meeting People', desc:'Привітання та знайомства англійською.', dur:'8 хв', status:'done', score:95, steps:[1,1,1,1] },
  { num:2, title:'Ordering Coffee', desc:'Замовлення у кафе та ресторані.', dur:'7 хв', status:'done', score:88, steps:[1,1,1,1] },
  { num:3, title:'Asking for Directions', desc:'Як запитати дорогу у місті.', dur:'9 хв', status:'done', score:76, steps:[1,1,1,1] },
  { num:4, title:'Hotel Check-in', desc:'Реєстрація у готелі, діалог на ресепшні.', dur:'8 хв', status:'current', score:null, steps:[1,1,0.5,0] },
  { num:5, title:'At the Airport', desc:'Реєстрація на рейс, паспортний контроль.', dur:'10 хв', status:'locked', score:null, steps:[0,0,0,0] },
  { num:6, title:'Buying Tickets', desc:'Купівля квитків на транспорт.', dur:'7 хв', status:'locked', score:null, steps:[0,0,0,0] },
  { num:7, title:'Restaurant Dialogue', desc:'Розмова з офіціантом, меню.', dur:'8 хв', status:'locked', score:null, steps:[0,0,0,0] },
  { num:8, title:'Shopping Basics', desc:'Покупки у магазині, запитання про ціну.', dur:'7 хв', status:'locked', score:null, steps:[0,0,0,0] },
  { num:9, title:'Taking a Taxi', desc:'Виклик таксі, пояснення маршруту.', dur:'6 хв', status:'locked', score:null, steps:[0,0,0,0] },
  { num:10, title:'At the Pharmacy', desc:'Як пояснити симптоми англійською.', dur:'8 хв', status:'locked', score:null, steps:[0,0,0,0] },
  { num:11, title:'Booking a Tour', desc:'Бронювання екскурсії, запитання.', dur:'7 хв', status:'locked', score:null, steps:[0,0,0,0] },
  { num:12, title:'Emergency Phrases', desc:'Фрази для екстрених ситуацій.', dur:'6 хв', status:'locked', score:null, steps:[0,0,0,0] },
  { num:13, title:'Small Talk', desc:'Невимушена розмова з незнайомцями.', dur:'8 хв', status:'locked', score:null, steps:[0,0,0,0] },
  { num:14, title:'Public Transport', desc:'Автобуси, метро, потяги.', dur:'7 хв', status:'locked', score:null, steps:[0,0,0,0] },
  { num:15, title:'Weather Talk', desc:'Розмова про погоду, плани на день.', dur:'6 хв', status:'locked', score:null, steps:[0,0,0,0] },
  { num:16, title:'Making Plans', desc:'Домовленість про зустріч.', dur:'7 хв', status:'locked', score:null, steps:[0,0,0,0] },
  { num:17, title:'Saying Goodbye', desc:'Прощання, подяка, побажання.', dur:'6 хв', status:'locked', score:null, steps:[0,0,0,0] },
  { num:18, title:'Course Review', desc:'Повторення ключових фраз курсу.', dur:'10 хв', status:'locked', score:null, steps:[0,0,0,0] }
];

const lessonWords = {
  1: [
    { en:'hello', ua:'привіт', ex:'Hello, nice to meet you!' },
    { en:'nice to meet you', ua:'приємно познайомитися', ex:'Nice to meet you too.' },
    { en:'my name is', ua:'мене звати', ex:'My name is Alex.' },
    { en:'how are you', ua:'як справи', ex:'How are you today?' },
    { en:'goodbye', ua:'до побачення', ex:'Goodbye, see you later!' }
  ],
  2: [
    { en:'coffee', ua:'кава', ex:'Can I have a coffee, please?' },
    { en:'receipt', ua:'чек', ex:'Can I have the receipt?' },
    { en:'tip', ua:'чайові', ex:'Should I leave a tip?' },
    { en:'to go', ua:'на виніс', ex:'One latte to go, please.' },
    { en:'sugar', ua:'цукор', ex:'No sugar, thank you.' }
  ],
  3: [
    { en:'directions', ua:'напрямки', ex:'Can you give me directions?' },
    { en:'turn left', ua:'поверніть ліворуч', ex:'Turn left at the corner.' },
    { en:'straight ahead', ua:'прямо', ex:'Go straight ahead for two blocks.' },
    { en:'crossroad', ua:'перехрестя', ex:'Stop at the crossroad.' },
    { en:'map', ua:'карта', ex:'Do you have a map?' }
  ]
};

/* ========== ICONS ========== */
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
);
const LockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
);
const ArrowRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
);
const BookIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/></svg>
);
const EyeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
);
const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
);
const InfoIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
);
const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" x2="16" y1="2" y2="6"/><line x1="8" x2="8" y1="2" y2="6"/><line x1="3" x2="21" y1="10" y2="10"/></svg>
);
const SettingsIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></svg>
);
const ChevronDown = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
);
const ChevronUp = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m18 15-6-6-6 6"/></svg>
);
const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
);
const GridIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="7" height="7" x="3" y="3" rx="1"/><rect width="7" height="7" x="14" y="3" rx="1"/><rect width="7" height="7" x="14" y="14" rx="1"/><rect width="7" height="7" x="3" y="14" rx="1"/></svg>
);
const PlusCircle = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v8"/><path d="M8 12h8"/></svg>
);
const UserIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 18a2 2 0 0 0-2-2H9a2 2 0 0 0-2 2"/><rect width="18" height="18" x="3" y="4" rx="2"/><circle cx="12" cy="10" r="2"/><line x1="8" x2="8" y1="2" y2="4"/><line x1="16" x2="16" y1="2" y2="4"/></svg>
);

/* ========== COMPONENT ========== */
const RoadMap = ({ authUser }) => {
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLesson, setDrawerLesson] = useState(null);
  const manageRef = useRef(null);
  const { t, i18n } = useTranslation();

  const stepNames = [t('Video'), t('Words'), t('Quiz'), t('Speaking')];

  // Manage Plan States
  const [planDrawerOpen, setPlanDrawerOpen] = useState(false);
  const [manageMode, setManageMode] = useState(''); // 'up', 'down', 'topic'
  const [selLevel, setSelLevel] = useState(null);
  const [selTopic, setSelTopic] = useState(null);
  const [topicStep, setTopicStep] = useState(1);
  const [showSuccess, setShowSuccess] = useState(false);

  const curLevelIndex = 1; // Currently hardcoded to A0->A1
  const curLevelData = LEVELS[curLevelIndex];

  const openPlanDrawer = (mode) => {
    setManageMode(mode);
    setSelLevel(null);
    setSelTopic(null);
    setTopicStep(1);
    setShowSuccess(false);
    setDropdownOpen(false);
    setPlanDrawerOpen(true);
  };

  const closePlanDrawer = () => {
    setPlanDrawerOpen(false);
  };

  const createCourse = () => {
    // const topic = selTopic ? TOPICS.find(t => t.id === selTopic) : null;
    // const l = LEVELS[selLevel || 0];
    setShowSuccess(true);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (manageRef.current && !manageRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const openWords = (num) => {
    setDrawerLesson(num);
    setDrawerOpen(true);
  };
  const closeWords = () => {
    setDrawerOpen(false);
    setDrawerLesson(null);
  };

  const currentLesson = lessons.find(l => l.status === 'current');
  const drawerWords = drawerLesson ? (lessonWords[drawerLesson] || []) : [];
  const drawerLessonData = drawerLesson ? lessons.find(l => l.num === drawerLesson) : null;

  return (
    <div className={css.roadMapPage}>
      <Header authUser={authUser} />

      {/* ===== HERO ===== */}
      <section className={css.hero}>
        <div className={`${css.heroOrb} ${css.o1}`}></div>
        <div className={`${css.heroOrb} ${css.o2}`}></div>
        <div className={css.heroInner}>

          {/* Course head */}
          <div className={css.courseHead}>
            <div className={css.courseInfo}>
              <div className={css.courseBadge}>
                <span className={css.courseBadgeDot}>
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
                </span>
                {t("Personal Plan")}
              </div>
              <h1 className={css.courseTitle}>Travel English: <span className={css.lvl}>A0 → A1</span></h1>
              <div className={css.courseMeta}>
                <span><CalendarIcon /> 18 {t("lessons")}</span>
                <span><ClockIcon /> ~6 {t("hours")}</span>
                <span>{t("Created based on your goal and level")}</span>
              </div>
            </div>

            {/* Manage dropdown */}
            <div className={css.manageWrap} ref={manageRef}>
              <button
                className={`${css.btnManage} ${dropdownOpen ? css.open : ''}`}
                onClick={() => setDropdownOpen(prev => !prev)}
              >
                <SettingsIcon />
                {t("Manage plan")}
                <ChevronDown />
              </button>
              <div className={`${css.dropdown} ${dropdownOpen ? css.show : ''}`}>
                <div className={css.ddItem} onClick={() => openPlanDrawer('up')}><ChevronUp /> {t('Change level: higher')}</div>
                <div className={css.ddItem} onClick={() => openPlanDrawer('down')}><ChevronDown /> {t('Change level: lower')}</div>
                <div className={css.ddSep}></div>
                <div className={css.ddItem} onClick={() => openPlanDrawer('topic')}><BookIcon /> {t('Change learning topic')}</div>
                <div className={css.ddSep}></div>
                <div className={`${css.ddItem} ${css.soon}`}>
                  <UserIcon /> {t('Study with tutor')}
                  <span className={css.ddSoon}>{t('Soon')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className={css.statsRow}>
            <div className={css.statCard}><div className={css.statValue}>65<span className={css.unit}>%</span></div><div className={css.statLabel}>{t("Course completed")}</div></div>
            <div className={css.statCard}><div className={css.statValue}>12</div><div className={css.statLabel}>{t("Words learned")}</div></div>
            <div className={css.statCard}><div className={css.statValue}>5</div><div className={css.statLabel}>{t("Words in Dictionary")}</div></div>
            <div className={css.statCard}><div className={css.statValue}>3</div><div className={css.statLabel}>{t("Lessons completed")}</div></div>
            <div className={css.statCard}><div className={css.statValue}>7</div><div className={css.statLabel}>{t("Day streak")} 🔥</div></div>
          </div>
        </div>
      </section>

      {/* ===== MANAGE PLAN DRAWER ===== */}
      <div className={`${css.drOv} ${planDrawerOpen ? css.show : ''}`} onClick={closePlanDrawer}></div>
      <div className={`${css.dr} ${planDrawerOpen ? css.show : ''}`}>
        
        {/* SUCCESS SCREEN */}
        {showSuccess ? (
          <div className={`${css.drSuc} ${css.show}`}>
            <div className={css.drSucIcon}><CheckIcon /></div>
            <div className={css.drSucH}>Новий курс створено!</div>
            <div className={css.drSucP}>
              {selTopic ? TOPICS.find(t => t.id === selTopic)?.title : 'Travel'} English {LEVELS[selLevel||0].from} → {LEVELS[selLevel||0].to} додано до розділу «Мої курси».
            </div>
            <div className={css.drSucNote}>Ваш попередній курс Travel English A0 → A1 і весь прогрес збережено.</div>
            <div className={css.drSucBtns}>
              <button className={`${css.drBtn} ${css.drBtnP}`} onClick={closePlanDrawer}>Перейти до нового курсу <ArrowRight /></button>
              <button className={`${css.drBtn} ${css.drBtnS}`} onClick={closePlanDrawer}>Повернутися до моїх курсів</button>
            </div>
          </div>
        ) : (
          <>
            {/* UP MODE */}
            {manageMode === 'up' && (
              <>
                <div className={css.drHead}>
                  <div className={css.drTop}>
                    <div className={css.drTitle}>Перейти на вищий рівень</div>
                    <div className={css.drClose} onClick={closePlanDrawer}><CloseIcon /></div>
                  </div>
                  <div className={css.drSub}>Оберіть рівень наступного курсу. Ми створимо новий курс, а ваш поточний прогрес збережеться.</div>
                  <div className={css.drCur}>
                    <div className={css.drCurLabel}>Поточний курс</div>
                    <div className={css.drCurTitle}>Travel English</div>
                    <div className={css.drCurLevel}>A0 → A1</div>
                    <div className={css.drCurProg}>65% курсу пройдено</div>
                  </div>
                </div>
                <div className={css.drBody}>
                  <div className={css.drQ}>До якого рівня перейти?</div>
                  <div className={css.drOpts}>
                    {LEVELS.map((l, i) => {
                      if (i <= curLevelIndex) return null;
                      const rec = i === curLevelIndex + 1;
                      return (
                        <div key={i} className={`${css.rc} ${selLevel === i ? css.sel : ''}`} onClick={() => setSelLevel(i)}>
                          <div className={css.rcR}></div>
                          <div className={css.rcBody}>
                            <div className={css.rcTitle}>{l.from} → {l.to}</div>
                            <div className={css.rcDesc}>{l.desc}</div>
                            {rec && <div className={css.rcBadge}>Рекомендовано</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className={css.drLink}><InfoIcon /> Не впевнені? Пройдіть короткий тест</div>
                </div>
                <div className={css.drFoot}>
                  <button className={`${css.drBtn} ${css.drBtnS}`} onClick={closePlanDrawer}>Скасувати</button>
                  <button className={`${css.drBtn} ${css.drBtnP} ${selLevel === null ? css.dis : ''}`} onClick={createCourse}>
                    Створити курс {selLevel !== null ? `${LEVELS[selLevel].from} → ${LEVELS[selLevel].to}` : ''} <ArrowRight />
                  </button>
                </div>
              </>
            )}

            {/* DOWN MODE */}
            {manageMode === 'down' && (
              <>
                <div className={css.drHead}>
                  <div className={css.drTop}>
                    <div className={css.drTitle}>Обрати простіший рівень</div>
                    <div className={css.drClose} onClick={closePlanDrawer}><CloseIcon /></div>
                  </div>
                  <div className={css.drSub}>Оберіть курс, який краще відповідає вашому поточному рівню. Ваш прогрес збережеться.</div>
                  <div className={css.drCur}>
                    <div className={css.drCurLabel}>Поточний курс</div>
                    <div className={css.drCurTitle}>Travel English</div>
                    <div className={css.drCurLevel}>A0 → A1</div>
                    <div className={css.drCurProg}>65% курсу пройдено</div>
                  </div>
                </div>
                <div className={css.drBody}>
                  <div className={css.drQ}>Оберіть рівень</div>
                  <div className={css.drOpts}>
                    {LEVELS.map((l, i) => {
                      if (i >= curLevelIndex) return null;
                      const rec = i === curLevelIndex - 1;
                      return (
                        <div key={i} className={`${css.rc} ${selLevel === i ? css.sel : ''}`} onClick={() => setSelLevel(i)}>
                          <div className={css.rcR}></div>
                          <div className={css.rcBody}>
                            <div className={css.rcTitle}>{l.from} → {l.to}</div>
                            <div className={css.rcDesc}>{l.desc}</div>
                            {rec && <div className={css.rcBadge}>Рекомендовано</div>}
                          </div>
                        </div>
                      );
                    }).reverse()}
                  </div>
                  <div className={css.drLink}><InfoIcon /> Не впевнені? Пройдіть короткий тест</div>
                </div>
                <div className={css.drFoot}>
                  <button className={`${css.drBtn} ${css.drBtnS}`} onClick={closePlanDrawer}>Скасувати</button>
                  <button className={`${css.drBtn} ${css.drBtnP} ${selLevel === null ? css.dis : ''}`} onClick={createCourse}>
                    Створити курс {selLevel !== null ? `${LEVELS[selLevel].from} → ${LEVELS[selLevel].to}` : ''} <ArrowRight />
                  </button>
                </div>
              </>
            )}

            {/* TOPIC MODE */}
            {manageMode === 'topic' && (
              <>
                {topicStep === 1 ? (
                  <>
                    <div className={css.drHead}>
                      <div className={css.drTop}>
                        <div className={css.drTitle}>Оберіть нову тему</div>
                        <div className={css.drClose} onClick={closePlanDrawer}><CloseIcon /></div>
                      </div>
                      <div className={css.drStep}>Крок 1 з 2</div>
                      <div className={css.drSub}>Ми створимо окремий курс. Ваш поточний курс і прогрес залишаться доступними.</div>
                      <div className={css.drCur}>
                        <div className={css.drCurLabel}>Поточний курс</div>
                        <div className={css.drCurTitle}>Travel English</div>
                        <div className={css.drCurLevel}>A0 → A1</div>
                        <div className={css.drCurProg}>65% курсу пройдено</div>
                      </div>
                    </div>
                    <div className={css.drBody}>
                      <div className={css.drQ}>Оберіть тему</div>
                      <div className={css.drOpts}>
                        {TOPICS.map((t) => (
                          <div key={t.id} className={`${css.tc} ${selTopic === t.id ? css.sel : ''}`} onClick={() => setSelTopic(t.id)}>
                            <div className={css.tcEmoji}>{t.emoji}</div>
                            <div className={css.tcBody}>
                              <div className={css.tcTitle}>{t.title}</div>
                              <div className={css.tcDesc}>{t.desc}</div>
                            </div>
                            <div className={css.tcCheck}><CheckIcon /></div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className={css.drFoot}>
                      <button className={`${css.drBtn} ${css.drBtnS}`} onClick={closePlanDrawer}>Скасувати</button>
                      <button className={`${css.drBtn} ${css.drBtnP} ${selTopic === null ? css.dis : ''}`} onClick={() => setTopicStep(2)}>
                        Далі: обрати рівень <ArrowRight />
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className={css.drHead}>
                      <div className={css.drTop}>
                        <div className={css.drTitle}>З якого рівня почати?</div>
                        <div className={css.drClose} onClick={closePlanDrawer}><CloseIcon /></div>
                      </div>
                      <div className={css.drStep}>Крок 2 з 2</div>
                      <div className={css.drSub}>Ми рекомендуємо рівень на основі вашого поточного прогресу.</div>
                    </div>
                    <div className={css.drBody}>
                      <div className={css.drQ}>Оберіть рівень</div>
                      <div className={css.drOpts}>
                        {LEVELS.map((l, i) => {
                          const rec = i === curLevelIndex;
                          return (
                            <div key={i} className={`${css.rc} ${selLevel === i ? css.sel : ''}`} onClick={() => setSelLevel(i)}>
                              <div className={css.rcR}></div>
                              <div className={css.rcBody}>
                                <div className={css.rcTitle}>{l.from} → {l.to}</div>
                                <div className={css.rcDesc}>{l.desc}</div>
                                {rec && <div className={css.rcBadge}>Рекомендовано</div>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className={css.drLink}><InfoIcon /> Не впевнені? Пройдіть тест</div>
                      <div className={css.drSummary}>
                        <div className={css.drSummaryTitle}>Новий курс</div>
                        <div className={css.drSummaryRow}><span className={css.sl}>Тема</span><span className={css.sv}>{selTopic && TOPICS.find(t=>t.id===selTopic)?.emoji} {selTopic && TOPICS.find(t=>t.id===selTopic)?.title}</span></div>
                        <div className={css.drSummaryRow}><span className={css.sl}>Стартовий рівень</span><span className={css.sv}>{selLevel !== null ? LEVELS[selLevel].from : '-'}</span></div>
                        <div className={css.drSummaryRow}><span className={css.sl}>План</span><span className={css.sv}>{selLevel !== null ? `${LEVELS[selLevel].from} → ${LEVELS[selLevel].to}` : '-'}</span></div>
                        <div className={css.drSummaryRow}><span className={css.sl}>Орієнтовно</span><span className={css.sv}>18 уроків</span></div>
                      </div>
                      <div className={css.drInfo}><InfoIcon /> Курс буде додано до «Мої курси». Попередній курс залишиться.</div>
                    </div>
                    <div className={css.drFoot}>
                      <button className={`${css.drBtn} ${css.drBtnS}`} onClick={() => setTopicStep(1)}>Назад</button>
                      <button className={`${css.drBtn} ${css.drBtnP} ${selLevel === null ? css.dis : ''}`} onClick={createCourse}>
                        Створити курс <ArrowRight />
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* ===== CONTINUE CARD ===== */}
      {currentLesson && (
        <div className={css.continueSection}>
          <div className={css.continueCard}>
            <div className={css.ccLeft}>
              <div className={css.ccTag}><span className={css.pulse}></span> {t("Continue Learning")}</div>
              <div className={css.ccTitle}>{t("Lesson")} {currentLesson.num}: {currentLesson.title}</div>
              <div className={css.ccLesson}>{t("You stopped at the step")}: <strong>{t("Quiz")}</strong></div>
              <div className={css.ccSteps}>
                {stepNames.map((name, i) => {
                  const val = currentLesson.steps[i];
                  const cls = val === 1 ? css.done : val === 0.5 ? css.active : css.upcoming;
                  return (
                    <span key={name} className={`${css.ccStep} ${cls}`}>
                      {val === 1 && <CheckIcon />}
                      {val === 0.5 && <PlusCircle />}
                      {name}
                    </span>
                  );
                })}
              </div>
              <div className={css.ccHelper}><InfoIcon /> {t("We will return you to where you left off.")}</div>
            </div>
            <div className={css.ccRight}>
              <button className={css.btnContinue} onClick={() => navigate('/trial-lesson')}>
                {t("Continue Learning")} <ArrowRight />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== LEARNING PATH ===== */}
      <section className={css.pathSection}>
        <div className={css.pathHeader}>
          <h2 className={css.pathTitle}>{t("Learning path")}</h2>
          <div className={css.pathRule}><InfoIcon /> {t("The next lesson unlocks after you pass the previous one.")}</div>
        </div>

        <div className={css.timeline}>
          {lessons.map((l) => {
            const isDone = l.status === 'done';
            const isCurrent = l.status === 'current';
            const isLocked = l.status === 'locked';

            return (
              <div className={css.lessonRow} key={l.num}>
                {/* Node */}
                <div className={css.lessonNode}>
                  <div className={`${css.nodeDot} ${isDone ? css.done : isCurrent ? css.current : css.locked}`}>
                    {isDone && <CheckIcon />}
                    {isLocked && <LockIcon />}
                  </div>
                  <span className={css.nodeNum}>{l.num}</span>
                </div>

                {/* Card */}
                <div className={`${css.lessonCard} ${isCurrent ? css.currentCard : ''} ${isLocked ? css.locked : ''}`}>
                  {isLocked && <div className={css.lcTooltip}>🔒 {t("Available after completing the lesson")} {l.num - 1}</div>}
                  {isCurrent && <span className={css.currentMarker}>📍 {t("You are here")}</span>}

                  <div className={css.lcTop}>
                    <span className={css.lcTitle}>{t("Lesson")} {l.num}: {l.title}</span>
                  </div>
                  <div className={css.lcDesc}>{l.desc}</div>

                  <div className={css.lcBottom}>
                    {/* Status badge */}
                    {isDone && <span className={`${css.lcStatus} ${css.stDone}`}><CheckIcon /> {t("Completed")}</span>}
                    {isCurrent && <span className={`${css.lcStatus} ${css.stCurrent}`}><ClockIcon />{t("In Progress")} </span>}
                    {isLocked && <span className={`${css.lcStatus} ${css.stLocked}`}><LockIcon /> {t("Blocked")}</span>}

                    {/* Score */}
                    {isDone && <span className={css.lcScore}>{l.score}%</span>}

                    {/* Actions */}
                    {isDone && (
                      <>
                        <button className={`${css.lcAction} ${css.actWords}`} onClick={(e) => { e.stopPropagation(); openWords(l.num); }}>
                          <BookIcon /> {t("Words")}
                        </button>
                        <button className={`${css.lcAction} ${css.actReview}`}>
                          <EyeIcon /> {t("Repeat")}
                        </button>
                      </>
                    )}
                    {isCurrent && (
                      <button className={`${css.lcAction} ${css.actContinue}`} onClick={() => navigate('/trial-lesson')}>
                        {t("Continue")} <ArrowRight />
                      </button>
                    )}
                    {isLocked && (
                      <span className={css.lcLockedMsg}><LockIcon /> {t("Complete lesson  ")} {l.num - 1}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Self study link */}
        <div className={css.selfStudy}>
          <p>{t("Would you like to learn without a fixed plan?")}</p>
          <a href="/allvideos">{t("Go to self-study")} <ArrowRight /></a>
        </div>
      </section>

      {/* ===== WORDS DRAWER ===== */}
      <div className={`${css.wdOv} ${drawerOpen ? css.show : ''}`} onClick={closeWords}></div>
      <div className={`${css.wdDrawer} ${drawerOpen ? css.show : ''}`}>
        <div className={css.wdHead}>
          <div className={css.wdHeadL}>
            <div className={css.wdHeadIcon}><BookIcon /></div>
            <div>
              <div className={css.wdTitle}>Слова уроку {drawerLesson}</div>
              <div className={css.wdSub}>{drawerLessonData ? `${drawerLessonData.title} · ${drawerWords.length} слів` : ''}</div>
            </div>
          </div>
          <button className={css.wdClose} onClick={closeWords}><CloseIcon /></button>
        </div>
        <div className={css.wdList}>
          {drawerWords.length === 0 ? (
            <div className={css.wdEmpty}>Немає слів для цього уроку</div>
          ) : (
            drawerWords.map((w, i) => (
              <div className={css.wdItem} key={i}>
                <div className={css.wdItemTop}>
                  <span className={css.wdEn}>{w.en}</span>
                  <span className={css.wdUa}>{w.ua}</span>
                </div>
                {w.ex && <div className={css.wdEx}>"{w.ex}"</div>}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default RoadMap;